import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canAccess } from "@/lib/permissions";
import { businessHeader } from "@/lib/outlet";
import { downloadReportPdf } from "@/lib/reportExport";
import { toast } from "sonner";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
    PieChart, Pie, Cell,
} from "recharts";
import {
    Calendar, IndianRupee, Users, TrendingUp, ChevronRight,
    Scissors, Check, X, Download, BarChart3,
} from "lucide-react";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

/* Golden + brown — salon luxury on cream #FAF9F6 */
const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";       /* Eyebrow labels & ornaments */
const GOLD_SOFT = "#D4AF37";  /* Dividers & leaf detail */
const CHOCOLATE = "#3D2817"; /* Greeting & weekday text */

/* Top services donut — maroon, rose, magenta, peach, gold */
const SERVICE_COLORS = ["#4A0E17", "#B85C7A", "#D9468F", "#F4A89A", "#C9A227"];

/* KPI cards — fresh icons + soft uplift wash */
const KPI_ICON = {
    revenue: {
        bg: "#FDF3DC", color: "#C9A227", sub: "#3D2817",
        wash: "linear-gradient(150deg, #FFFCF6 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(201,162,39,0.14) 0%, transparent 52%)",
        accent: "#C9A227",
    },
    bookings: {
        bg: "#E0EDFA", color: "#3B82C4", sub: "#2E6BA8",
        wash: "linear-gradient(150deg, #F5F9FE 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(59,130,196,0.12) 0%, transparent 52%)",
        accent: "#3B82C4",
    },
    customers: {
        bg: "#FBE4EF", color: "#D9468F", sub: "#B83A72",
        wash: "linear-gradient(150deg, #FDF6F9 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(217,70,143,0.11) 0%, transparent 52%)",
        accent: "#D9468F",
    },
    staff: {
        bg: "#DCEEDC", color: "#2D9B6E", sub: "#1F7A55",
        wash: "linear-gradient(150deg, #F4FBF7 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(45,155,110,0.11) 0%, transparent 52%)",
        accent: "#2D9B6E",
    },
    services: {
        bg: "#FCE8E4", color: "#C84B31", sub: "#9A4A32",
        wash: "linear-gradient(150deg, #FFF8F6 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(200,75,49,0.12) 0%, transparent 52%)",
        accent: "#C84B31",
    },
};

/* Today's appointment status — bar chart palette */
const STATUS_SERIES = [
    {
        key: "booked",
        label: "Booked",
        merge: ["booked", "confirmed"],
        color: "#4A0E17",
        gradient: ["#7A2438", "#4A0E17"],
        icon: Calendar,
    },
    {
        key: "in_progress",
        label: "In Progress",
        merge: ["in-service", "arrived"],
        color: "#D9468F",
        gradient: ["#F06BA8", "#D9468F"],
        icon: Scissors,
    },
    {
        key: "completed",
        label: "Completed",
        merge: ["completed"],
        color: "#C9A227",
        gradient: ["#E8C04A", "#C9A227"],
        icon: Check,
    },
    {
        key: "cancelled",
        label: "Cancelled",
        merge: ["cancelled", "no-show"],
        color: "#B8859C",
        gradient: ["#CEA3B5", "#A87088"],
        icon: X,
    },
];

function buildStatusData(breakdown) {
    const counts = Object.fromEntries(
        (breakdown || []).map(({ status, count }) => [status, count])
    );

    return STATUS_SERIES.map((series) => ({
        ...series,
        count: series.merge.reduce((sum, status) => sum + (counts[status] || 0), 0),
    }));
}

function getStatusYMax(maxCount) {
    if (maxCount <= 0) return 20;
    const step = maxCount <= 10 ? 5 : 10;
    return Math.max(step * 4, Math.ceil(maxCount / step) * step);
}

function AppointmentStatusChart({ breakdown }) {
    const data = buildStatusData(breakdown);
    const peak = Math.max(...data.map((d) => d.count), 0);
    const yMax = getStatusYMax(peak);
    const yTicks = [0, 1, 2, 3, 4].map((i) => Math.round((yMax / 4) * (4 - i)));
    const PLOT_H = 160;
    const FOOTER_H = 56;

    return (
        <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-6">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />

            <div className="relative z-10 mb-6">
                <h2 className="text-xl font-bold text-[#4A0E17] sm:text-2xl">
                    Today&apos;s appointment status
                </h2>
                <div className="mt-2 flex max-w-[220px] items-center">
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                </div>
            </div>

            <div className="flex gap-3 sm:gap-4">
                <div
                    className="flex w-7 shrink-0 flex-col justify-between pt-0.5 text-[11px] font-semibold tabular-nums text-[#8A8178]"
                    style={{ height: PLOT_H }}
                >
                    {yTicks.map((tick) => (
                        <span key={tick}>{tick}</span>
                    ))}
                </div>

                <div className="relative min-w-0 flex-1">
                    <div
                        className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between"
                        style={{ height: PLOT_H }}
                    >
                        {yTicks.map((tick) => (
                            <div
                                key={tick}
                                className="border-t border-dashed border-[#E8E4DE]"
                                aria-hidden
                            />
                        ))}
                    </div>

                    <div
                        className="pointer-events-none absolute inset-x-0 border-t border-[#E8E4DE]/90"
                        style={{ top: PLOT_H }}
                        aria-hidden
                    />

                    <div
                        className="relative flex justify-around gap-2 px-1 sm:gap-4 sm:px-2"
                        style={{ height: PLOT_H + FOOTER_H }}
                    >
                        {data.map((item) => {
                            const Icon = item.icon;
                            const barH = yMax > 0
                                ? Math.max(item.count > 0 ? 10 : 0, (item.count / yMax) * PLOT_H)
                                : 0;

                            return (
                                <div
                                    key={item.key}
                                    className="flex max-w-[72px] flex-1 flex-col items-center sm:max-w-[80px]"
                                >
                                    <div
                                        className="relative flex w-full flex-col items-center justify-end"
                                        style={{ height: PLOT_H }}
                                    >
                                        <span
                                            className="mb-1.5 text-sm font-extrabold tabular-nums"
                                            style={{ color: item.color }}
                                        >
                                            {item.count}
                                        </span>
                                        <div
                                            className="w-full rounded-t-lg transition-all duration-500"
                                            style={{
                                                height: barH,
                                                background: `linear-gradient(180deg, ${item.gradient[0]} 0%, ${item.gradient[1]} 100%)`,
                                                boxShadow: item.count > 0 ? `0 4px 14px ${item.color}33` : "none",
                                            }}
                                        />
                                        <div
                                            className="absolute bottom-0 left-1/2 z-10 flex size-10 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_4px_14px_rgba(45,41,38,0.14)] ring-2 ring-white"
                                            style={{ boxShadow: `0 4px 14px rgba(45,41,38,0.12), 0 0 0 1px ${item.color}22` }}
                                        >
                                            <Icon
                                                className="size-[17px]"
                                                style={{ color: item.color }}
                                                strokeWidth={2.25}
                                            />
                                        </div>
                                    </div>

                                    <span className="mt-7 text-center text-[11px] font-bold leading-tight text-[#3D2817] sm:text-xs">
                                        {item.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
}

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div
                className="h-px flex-1"
                style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }}
            />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>
                ◆
            </span>
            <div
                className="h-px flex-1"
                style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }}
            />
        </div>
    );
}

const HEADER_TITLE =
    "text-[1.25rem] font-semibold leading-snug tracking-tight whitespace-nowrap sm:text-[1.375rem]";

const REPORT_BTN =
    "inline-flex h-8 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-[#E8E4DE] bg-white px-2.5 text-[11px] font-semibold leading-none text-[#3D2817] shadow-sm transition-colors hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9] disabled:opacity-50";

function DashboardHeader({ firstName, user }) {
    const greeting = getGreeting();
    const now = new Date();
    const weekday = now.toLocaleDateString("en-GB", { weekday: "long" });
    const dayMonth = now.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
    const showReports = canAccess(user?.role, "reports");

    return (
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
                <p className={HEADER_TITLE}>
                    <span style={{ color: CHOCOLATE }}>{weekday}</span>{" "}
                    <span style={{ color: GOLD }}>{dayMonth}</span>
                </p>
                <h1 className="mt-1.5 text-[1.375rem] font-semibold leading-snug sm:text-[1.5rem]">
                    <span style={{ color: CHOCOLATE }}>{greeting},</span>{" "}
                    <span style={{ color: GOLD }}>{firstName}</span>
                </h1>
                <GoldDivider className="mt-3 max-w-[200px]" />
            </div>
            {showReports && <DashboardReportPanel user={user} />}
        </header>
    );
}

function DashboardReportPanel({ user }) {
    const [busy, setBusy] = useState(false);

    const onDownload = async () => {
        setBusy(true);
        try {
            const [reportRes, outletsRes] = await Promise.all([
                api.get("/reports/revenue", { params: { days: 30 } }),
                api.get("/outlets").catch(() => ({ data: [] })),
            ]);
            await downloadReportPdf(reportRes.data, businessHeader(outletsRes.data, user));
        } catch {
            toast.error("Could not download report PDF");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="flex w-full max-w-[200px] flex-col sm:ml-auto"
            data-testid="dashboard-report-links"
        >
            <p className={`${HEADER_TITLE} text-left sm:text-right`}>
                <span style={{ color: CHOCOLATE }}>Report</span>
            </p>
            <div className="mt-2.5 flex w-full gap-2">
                <Link to="/app/reports" className={REPORT_BTN} data-testid="dashboard-open-reports">
                    <BarChart3 className="size-3.5 shrink-0 text-[#D4AF37]" strokeWidth={2.25} />
                    View
                </Link>
                <button
                    type="button"
                    className={REPORT_BTN}
                    disabled={busy}
                    onClick={onDownload}
                    data-testid="dashboard-download-report"
                >
                    <Download className="size-3.5 shrink-0 text-[#D4AF37]" strokeWidth={2.25} />
                    {busy ? "…" : "PDF"}
                </button>
            </div>
            <GoldDivider className="mt-3 w-full" />
        </div>
    );
}

function RevenueDot({ cx, cy }) {
    if (cx == null || cy == null) return null;
    return (
        <g>
            <circle cx={cx} cy={cy} r={6} fill="#FFF8E7" stroke="#4A0E17" strokeWidth={2} />
            <circle cx={cx} cy={cy} r={2.5} fill="#D4AF37" />
        </g>
    );
}

function RevenuePointLabel({ x, y, value }) {
    if (x == null || y == null || value == null) return null;
    return (
        <text x={x} y={y - 14} textAnchor="middle" fill="#4A0E17" fontSize={11} fontWeight={700}>
            {inr(value)}
        </text>
    );
}

function fmtTrendDay(dateStr) {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-GB", { weekday: "short" });
}

function RevenueChart({ trend }) {
    const chartHeight = 280;
    const points = trend.length || 7;
    const chartScrollWidth = Math.max(680, points * 88);

    const chartData = trend.map((d) => ({ ...d, label: fmtTrendDay(d.date) }));

    const chart = (
        <ResponsiveContainer width={chartScrollWidth} height={chartHeight}>
            <AreaChart data={chartData} margin={{ top: 24, right: 28, left: 4, bottom: 0 }}>
                <defs>
                    <linearGradient id="revenueAreaFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4A0E17" stopOpacity={0.32} />
                        <stop offset="100%" stopColor="#4A0E17" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#E8E4DE" vertical={false} />
                <XAxis
                    dataKey="label"
                    stroke="#8A8178"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    dy={8}
                />
                <YAxis
                    domain={[0, "auto"]}
                    stroke="#8A8178"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `₹${v / 1000}K` : `₹${v}`)}
                    width={48}
                />
                <Tooltip
                    contentStyle={{
                        background: "#fff",
                        border: "1px solid #E8E4DE",
                        borderRadius: 8,
                        fontSize: 12,
                        color: "#4A0E17",
                    }}
                    formatter={(v) => [inr(v), "Revenue"]}
                    labelStyle={{ color: "#706B66", fontWeight: 700 }}
                />
                <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#4A0E17"
                    strokeWidth={2.5}
                    fill="url(#revenueAreaFill)"
                    dot={<RevenueDot />}
                    activeDot={false}
                >
                    <LabelList dataKey="revenue" content={<RevenuePointLabel />} />
                </Area>
            </AreaChart>
        </ResponsiveContainer>
    );

    return (
        <div className="relative min-w-0 overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-6">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />

            <div className="mb-6">
                <h2 className="text-xl font-bold text-[#4A0E17] sm:text-2xl">
                    Revenue · last 7 days
                </h2>
                <div className="mt-2 flex max-w-[180px] items-center">
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                </div>
            </div>

            <div className="scrollbar-chart scrollbar-chart-x -mx-1 px-1 pb-3 pt-0.5">
                <div style={{ width: chartScrollWidth, height: chartHeight }}>
                    {chart}
                </div>
            </div>
        </div>
    );
}

function TopServicesChart({ services }) {
    const total = services.reduce((sum, s) => sum + s.count, 0);
    const chartData = services.map((s, i) => ({
        ...s,
        color: SERVICE_COLORS[i % SERVICE_COLORS.length],
        pct: total ? Math.round((s.count / total) * 100) : 0,
    }));

    return (
        <div className="relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-6">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />

            <div className="mb-5">
                <h2 className="text-xl font-bold text-[#4A0E17] sm:text-2xl">
                    Top services
                </h2>
                <div className="mt-2 flex max-w-[120px] items-center">
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                </div>
            </div>

            {chartData.length === 0 ? (
                <p className="flex flex-1 items-center text-sm font-semibold text-[#706B66]">
                    No data yet — start invoicing to see trends.
                </p>
            ) : (
                <>
                    <div className="scrollbar-chart scrollbar-chart-x -mx-1 min-h-0 flex-1 px-1 pb-3 pt-0.5">
                        <div className="flex min-w-[360px] flex-col items-center gap-4 sm:min-w-[480px] sm:flex-row sm:items-stretch">
                            <div className="relative h-[148px] w-[148px] shrink-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={chartData}
                                            dataKey="count"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={46}
                                            outerRadius={68}
                                            paddingAngle={2}
                                            stroke="#fff"
                                            strokeWidth={3}
                                        >
                                            {chartData.map((entry) => (
                                                <Cell key={entry.name} fill={entry.color} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="pointer-events-none absolute left-1/2 top-1/2 w-[72px] -translate-x-1/2 -translate-y-1/2 text-center">
                                    <p className="text-[10px] font-semibold leading-none text-[#8A8178]">Total</p>
                                    <p className="mt-1 text-[1.375rem] font-bold leading-none text-[#4A0E17]">
                                        {total}
                                    </p>
                                    <p className="mt-1 text-[10px] font-semibold leading-none text-[#8A8178]">Services</p>
                                </div>
                            </div>

                            <div className="scrollbar-chart scrollbar-chart-y min-h-0 max-h-[200px] w-full min-w-[200px] flex-1 pr-1 sm:w-auto">
                                {chartData.map((s, i) => (
                                    <div
                                        key={s.name}
                                        className={`flex items-center justify-between gap-3 py-2.5 ${
                                            i < chartData.length - 1 ? "border-b border-[#E8E4DE]" : ""
                                        }`}
                                    >
                                        <div className="flex min-w-0 items-center gap-2.5">
                                            <span
                                                className="size-2.5 shrink-0 rounded-full"
                                                style={{ backgroundColor: s.color }}
                                            />
                                            <span className="truncate text-[13px] font-semibold text-[#4A0E17]">
                                                {s.name}
                                            </span>
                                        </div>
                                        <span className="shrink-0 text-[13px] font-bold tabular-nums text-[#4A0E17]">
                                            {s.pct}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-5 flex justify-end border-t border-[#E8E4DE] pt-4">
                        <Link
                            to="/app/services"
                            className="inline-flex items-center gap-1 rounded-full border border-[#E8E4DE] bg-[#FDF8F6] px-4 py-2 text-[13px] font-bold text-[#4A0E17] transition-colors hover:border-[#D4AF37]/40 hover:bg-[#F5F0E8]"
                        >
                            View all services
                            <ChevronRight className="size-4 text-[#8A8178]" />
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub, iconTone = "revenue", to }) {
    const theme = KPI_ICON[iconTone] ?? KPI_ICON.revenue;

    const card = (
        <div
            className="group relative flex h-full min-h-[148px] flex-col overflow-hidden rounded-xl border border-[#E8E4DE]/80 p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D4AF37]/30 hover:shadow-[0_12px_32px_rgba(45,41,38,0.08)] sm:min-h-[156px] sm:p-6"
            style={{ background: theme.wash }}
        >
            <div
                className="pointer-events-none absolute -right-3 -top-3 size-24"
                style={{ background: theme.glow }}
                aria-hidden
            />
            <div
                className="absolute inset-x-0 top-0 h-[2px]"
                style={{
                    background: `linear-gradient(90deg, #4A0E17 0%, ${theme.accent} 50%, #4A0E17CC 100%)`,
                }}
                aria-hidden
            />

            <div className="relative flex shrink-0 items-start justify-between gap-2">
                <p className="min-h-[2.75rem] max-w-[calc(100%-2.75rem)] pt-0.5 text-[11px] font-extrabold uppercase leading-snug tracking-[0.14em] text-[#3D2817]">
                    {label}
                </p>
                <div
                    className="flex size-10 shrink-0 items-center justify-center rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] ring-1 ring-white/90 transition-transform duration-300 group-hover:scale-105"
                    style={{ backgroundColor: theme.bg }}
                >
                    <Icon className="size-[18px]" style={{ color: theme.color }} strokeWidth={2.25} />
                </div>
            </div>

            <p className="relative mt-3 shrink-0 truncate text-[1.875rem] font-extrabold leading-none tracking-tight tabular-nums text-[#3D2817] sm:text-[2rem]">
                {value}
            </p>

            <p
                className="relative mt-auto min-h-[1.25rem] truncate pt-2.5 text-[12px] font-extrabold tracking-wide sm:text-[13px]"
                style={{ color: sub ? theme.sub : "transparent" }}
            >
                {sub || "\u00A0"}
            </p>

            {to && (
                <ChevronRight className="absolute bottom-5 right-5 size-4 text-[#8A8178] opacity-0 transition-opacity group-hover:opacity-100 sm:bottom-6 sm:right-6" />
            )}
        </div>
    );

    if (!to) return card;

    return (
        <Link to={to} className="block h-full rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37]/60">
            {card}
        </Link>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    }, []);

    const firstName = user?.full_name?.split(" ")[0] || "there";

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="dashboard-page">
            <DashboardHeader firstName={firstName} user={user} />

            {!data ? (
                <div className="text-muted-foreground">Loading dashboard…</div>
            ) : (
                <>
            <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <StatCard
                    icon={IndianRupee}
                    iconTone="revenue"
                    label="Today's revenue"
                    value={inr(data.today_revenue)}
                    sub={`${inr(data.week_revenue)} this week`}
                    to="/app/reports"
                />
                <StatCard
                    icon={Calendar}
                    iconTone="bookings"
                    label="Today's bookings"
                    value={data.today_bookings}
                    sub="Including walk-ins"
                    to="/app/calendar"
                />
                <StatCard
                    icon={Users}
                    iconTone="customers"
                    label="Total customers"
                    value={data.total_customers}
                    sub={`+${data.new_customers_week} this week`}
                    to="/app/customers"
                />
                <StatCard
                    icon={TrendingUp}
                    iconTone="staff"
                    label="Active staff"
                    value={data.total_staff}
                    sub="On your team"
                    to="/app/staff"
                />
                <StatCard
                    icon={Scissors}
                    iconTone="services"
                    label="Total services"
                    value={data.total_services ?? 0}
                    sub="In your catalog"
                    to="/app/services"
                />
            </div>

            <div className="grid min-w-0 items-stretch gap-5 lg:grid-cols-3">
                <div className="min-w-0 lg:col-span-2">
                    <RevenueChart trend={data.trend} />
                </div>

                <div className="min-w-0">
                    <TopServicesChart services={data.top_services} />
                </div>
            </div>

            <AppointmentStatusChart breakdown={data.status_breakdown} />
                </>
            )}
        </div>
    );
}
